import { callAgent } from './aiCall'
import type { AiAgent } from '../store/aiSettings'

const HANDLE_SUGGEST_PROMPT = `You generate fresh Bluesky handles for a new account the operator is about to create on bsky.app/signup.

The operator will give you a "schema" hint plus a vibe. Produce 10 candidate handles, each on its own line, each prefixed with "@". All handles MUST end in ".bsky.social" (the default Bluesky PDS domain). Append a 3-digit random number to whichever handles fit the schema better with one — variety is good, not every line needs a number.

Schemas you may see:
  • "x-y-z"           — three single letters joined by dashes (e.g. @a-o-e.bsky.social)
  • "word-number"     — a short word + 3-digit number (e.g. @raven-318.bsky.social)
  • "two-words"       — two short words joined by a dash (e.g. @still-tide.bsky.social)
  • "free"            — anything memorable, lowercase, &lt;= 18 chars before the dot

Constraints (ignore these and the operator can't actually use the handle):
  • lowercase a-z, 0-9, single dashes between segments
  • no double dashes, no leading/trailing dash
  • &lt;= 18 chars before ".bsky.social"
  • must be plausible to be free (don't suggest things like @alice or @bob)

Output: 10 lines, each "@handle.bsky.social — one-line vibe note" — nothing else, no preamble.`

interface SuggestArgs {
  agent: AiAgent
  schema: string
  vibe?: string
}

const HANDLE_RE = /@([a-z0-9][a-z0-9-]{1,17}\.bsky\.social)/gi

/**
 * Ask the configured agent for 10 fresh handle candidates. Falls
 * back to a small client-side generator if the agent returns nothing
 * usable so the UI never shows an empty list. The human still does
 * captcha + email + password on bsky.app — this is just a creative
 * suggestion tool.
 */
export async function suggestHandles({
  agent,
  schema,
  vibe,
}: SuggestArgs): Promise<string[]> {
  const payload = JSON.stringify(
    {
      schema: schema.trim() || 'free',
      vibe: (vibe ?? '').trim() || 'neutral',
      generatedAt: new Date().toISOString(),
    },
    null,
    2,
  )
  const md = await callAgent(agent, HANDLE_SUGGEST_PROMPT, payload)
  const out = new Set<string>()
  HANDLE_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = HANDLE_RE.exec(md)) != null) {
    out.add(m[1].toLowerCase())
  }
  if (out.size === 0) {
    // Last-ditch — generator picks 5 plausible handles so the UI
    // shows something rather than an empty list.
    const seeds = ['raven', 'tide', 'echo', 'ember', 'opal']
    for (const s of seeds) {
      const n = Math.floor(100 + Math.random() * 900)
      out.add(`${s}-${n}.bsky.social`)
    }
  }
  return Array.from(out).slice(0, 10)
}

/**
 * Build a `javascript:` bookmarklet that, when clicked on bsky.app
 * (logged out), drives the signup flow as far as the captcha:
 *
 *   1. Welcome modal → click "Create account" (if visible)
 *   2. Step 1 → fill emailInput / passwordInput / date, click nextBtn
 *   3. Step 2 → fill handleInput, click nextBtn
 *   4. Step 3 → human solves the captcha + clicks Submit
 *
 * Polls between steps so the form has time to render. React-
 * Native-Web inputs are controlled, so values are written via the
 * prototype-descriptor setter + dispatched input/change events
 * (verified working with Playwright).
 *
 * Lives as a draggable `javascript:` URL — the only way to reach
 * into a cross-origin window. Pasting `javascript:` into the
 * address bar gets sanitised in modern browsers.
 */
export function buildSignupBookmarklet(opts: {
  email?: string
  password?: string
  handle?: string
  /** YYYY-MM-DD; defaults to a reasonable adult dob. */
  birthDate?: string
}): string {
  const handleLocal = (opts.handle ?? '').replace(/\.bsky\.social$/i, '')
  const payload = {
    email: opts.email ?? '',
    pw: opts.password ?? '',
    handle: handleLocal,
    dob: opts.birthDate ?? '1990-05-09',
  }
  // JSON.stringify handles all the escaping we need; the body just
  // parses it once and runs the multi-step flow.
  const json = JSON.stringify(payload)
  const body = `(async function(){
  var P=${json};
  function set(el,v){
    var d=Object.getOwnPropertyDescriptor(HTMLInputElement.prototype,'value');
    if(d&&d.set){d.set.call(el,v);}
    el.dispatchEvent(new Event('input',{bubbles:true}));
    el.dispatchEvent(new Event('change',{bubbles:true}));
  }
  function $(sel){return document.querySelector(sel);}
  function waitFor(sel,timeout){
    return new Promise(function(res,rej){
      var deadline=Date.now()+(timeout||8000);
      (function tick(){
        var el=$(sel);
        if(el)return res(el);
        if(Date.now()>deadline)return rej(new Error('timeout '+sel));
        setTimeout(tick,150);
      })();
    });
  }
  function sleep(ms){return new Promise(function(r){setTimeout(r,ms);});}
  try {
    // 0. Welcome modal — find "Create account" inside any open dialog.
    var dlg=document.querySelector('[role="dialog"]');
    if(dlg){
      var btns=Array.prototype.slice.call(dlg.querySelectorAll('[aria-label="Create account"], [role="button"]'));
      var welcomeBtn=null;
      for(var i=0;i<btns.length;i++){
        var b=btns[i];
        var t=(b.textContent||'').trim();
        if(/create account/i.test(t)||(b.getAttribute('aria-label')||'')==='Create account'){
          welcomeBtn=b;break;
        }
      }
      if(welcomeBtn){welcomeBtn.click();await sleep(400);}
    }
    // 1. Step 1 — fill email + password + dob, click Next.
    if(P.email||P.pw){
      var emailEl=await waitFor('[data-testid="emailInput"]',8000).catch(function(){return null;});
      if(emailEl){
        if(P.email)set(emailEl,P.email);
        var pwEl=$('[data-testid="passwordInput"]');
        if(pwEl&&P.pw)set(pwEl,P.pw);
        var dobEl=$('[data-testid="date"]');
        if(dobEl&&P.dob)set(dobEl,P.dob);
        var n1=$('[data-testid="nextBtn"]');
        if(n1){await sleep(150);n1.click();}
      }
    }
    // 2. Step 2 — fill handle, then wait for the async availability
    //    check to settle before clicking Next (otherwise the click
    //    fires while the field is still validating and gets swallowed).
    if(P.handle){
      var hEl=await waitFor('[data-testid="handleInput"]',8000).catch(function(){return null;});
      if(hEl){
        set(hEl,P.handle);
        // Give Bluesky's resolveHandle round-trip up to 4s. We poll
        // for a captcha iframe appearing AFTER each click as the
        // success signal; if it doesn't, retry the click.
        var clicked=false;
        for(var attempt=0;attempt<6&&!clicked;attempt++){
          await sleep(700);
          var n2=$('[data-testid="nextBtn"]');
          if(!n2)continue;
          n2.click();
          // Did we advance? Look for the captcha or step-3 marker.
          for(var w=0;w<10;w++){
            await sleep(200);
            if(document.querySelector('#captcha-iframe')){clicked=true;break;}
            if(/Step\\s*3\\s*of\\s*3/i.test(document.body.innerText||'')){clicked=true;break;}
          }
        }
      }
    }
  } catch(err) {
    alert('bskyhq filler error: '+(err&&err.message?err.message:err));
    return;
  }
})();`
  return 'javascript:' + encodeURI(body)
}

/**
 * Build a self-contained Node ESM script the operator can download
 * and run locally with Playwright. Walks bsky.app's signup flow
 * automatically through Step 1 + Step 2, then PAUSES on the hCaptcha
 * step so the human can solve it manually in the visible browser.
 *
 * Prereq: `npm i -D playwright && npx playwright install chromium`
 * once on the machine. After that:  `node signup-foo.mjs`.
 *
 * Selectors verified against bsky.app on 2026-05-09 via the
 * operator's own Playwright recording.
 */
export function buildSignupScript(opts: {
  email: string
  password: string
  handle: string
  /** ISO date YYYY-MM-DD. Defaults to a reasonable adult dob. */
  birthDate?: string
}): string {
  const handleLocal = (opts.handle ?? '').replace(/\.bsky\.social$/i, '')
  const esc = (s: string) =>
    s.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n')
  const birthDate = opts.birthDate ?? '1990-05-09'
  return `// signup script for @${handleLocal}.bsky.social
// generated by bskyhq · run with:  node signup-${handleLocal}.mjs
//
// prereq (one-time):
//   npm i -D playwright
//   npx playwright install chromium
//
// the script fills bsky.app's Step 1 + Step 2 of 3 automatically,
// then PAUSES on the hCaptcha — solve it in the visible browser,
// click "Next" / "Submit", and the account is created. Then come
// back to bskyhq, sign in with an app password.

import { chromium } from 'playwright'

const EMAIL = '${esc(opts.email ?? '')}'
const PASSWORD = '${esc(opts.password ?? '')}'
const HANDLE = '${esc(handleLocal)}'         // local part only — ".bsky.social" appended by the form
const BIRTH_DATE = '${esc(birthDate)}'        // YYYY-MM-DD

if (!EMAIL || !PASSWORD || !HANDLE) {
  console.error('missing EMAIL / PASSWORD / HANDLE — edit the constants above.')
  process.exit(1)
}

console.log('▶ launching chromium (this may take a few seconds) …')
let browser
try {
  browser = await chromium.launch({ headless: false, slowMo: 50 })
} catch (e) {
  console.error('✕ chromium failed to launch: ' + (e?.message || e))
  console.error('  try running:  npx playwright install chromium')
  process.exit(1)
}
console.log('✓ chromium launched.')
const ctx = await browser.newContext({ viewport: { width: 720, height: 1000 } })
const page = await ctx.newPage()

console.log('▶ opening bsky.app …')
await page.goto('https://bsky.app/')

console.log('▶ welcome modal → Create account')
await page
  .getByRole('dialog')
  .getByRole('button', { name: 'Create account' })
  .click()

console.log('▶ Step 1 of 3 — filling email + password + dob')
await page.getByTestId('emailInput').fill(EMAIL)
await page.getByTestId('passwordInput').fill(PASSWORD)
await page.getByTestId('date').fill(BIRTH_DATE)
await page.getByTestId('nextBtn').click()

console.log('▶ Step 2 of 3 — filling handle')
await page.getByTestId('handleInput').fill(HANDLE)
// Bluesky validates handle availability async; click can land
// while the field is still validating and get swallowed. Poll-and-
// retry until the captcha shows up or we give up.
let advanced = false
for (let attempt = 0; attempt < 6 && !advanced; attempt++) {
  await page.waitForTimeout(700)
  await page.getByTestId('nextBtn').click().catch(() => {})
  for (let w = 0; w < 10; w++) {
    await page.waitForTimeout(200)
    if ((await page.locator('#captcha-iframe').count()) > 0) {
      advanced = true
      break
    }
  }
}

console.log('▶ Step 3 of 3 — hCaptcha. Trying the invisible-mode checkbox …')
try {
  await page
    .locator('#captcha-iframe')
    .contentFrame()
    .locator('iframe[title="Widget containing checkbox for hCaptcha security challenge"]')
    .contentFrame()
    .getByRole('checkbox', { name: /I am human/i })
    .click({ timeout: 5000 })
  console.log('  clicked. captcha may still show a challenge — solve it in the browser.')
} catch (e) {
  console.log('  could not click the checkbox automatically — solve it in the browser.')
}

console.log('')
console.log('=================================================================')
console.log('  HUMAN STEP: solve any hCaptcha challenge in the open browser, ')
console.log('  click "Next" / "Submit" to finish account creation.')
console.log('  When done, close the browser window — the script will exit.')
console.log('=================================================================')
console.log('')

await browser.waitForEvent('disconnected')
console.log('▶ browser closed. account should be live; come back to bskyhq and add it via app password.')
process.exit(0)
`
}

/**
 * Build a self-contained batch signup script. Runs N signups in
 * SERIAL — fresh chromium.launch() per signup so each starts cold
 * (no persistent state = "signed out", no private mode needed). The
 * operator solves a captcha per signup; closing the browser window
 * advances to the next candidate.
 */
export function buildBatchSignupScript(opts: {
  candidates: { email: string; password: string; handle: string }[]
  /** YYYY-MM-DD; defaults to a reasonable adult dob applied to all. */
  birthDate?: string
}): string {
  const dob = opts.birthDate ?? '1990-05-09'
  const list = opts.candidates.map((c) => ({
    email: c.email,
    pw: c.password,
    handle: (c.handle ?? '').replace(/\.bsky\.social$/i, ''),
    dob,
  }))
  const json = JSON.stringify(list, null, 2)
  const total = list.length
  return `// bskyhq batch signup — runs ${total} signups in series.
// generated ${new Date().toISOString()}
//
// prereq (one-time):
//   npm i -D playwright
//   npx playwright install chromium
//
// run with:
//   node signup-batch.mjs
//
// For each candidate:
//   1. Launches a FRESH chromium browser (cold, signed-out, no
//      cookies inherited from anywhere)
//   2. Drives Welcome modal → Step 1 → Step 2 of bsky.app's signup
//   3. Pauses on the hCaptcha — human solves it + clicks Submit
//   4. CLOSE the browser window when done with that signup; the
//      script then opens the next browser for the next candidate.
//
// Captchas are the only thing you do. Everything else is automatic.

import { chromium } from 'playwright'

const CANDIDATES = ${json}

console.log('▶ batch starting — ' + CANDIDATES.length + ' signups in series.')
console.log('▶ for each: solve captcha + click Submit + CLOSE the browser window to advance.')
console.log('')

let succeeded = 0
let aborted = 0

for (let i = 0; i < CANDIDATES.length; i++) {
  const c = CANDIDATES[i]
  console.log('──── ' + (i + 1) + ' / ' + CANDIDATES.length + ': @' + c.handle + '.bsky.social ────')
  console.log('  email:    ' + c.email)
  console.log('  password: ' + c.pw)
  console.log('  dob:      ' + c.dob)

  const browser = await chromium.launch({ headless: false, slowMo: 40 })
  const ctx = await browser.newContext({ viewport: { width: 520, height: 900 } })
  const page = await ctx.newPage()

  try {
    await page.goto('https://bsky.app/')
    await page
      .getByRole('dialog')
      .getByRole('button', { name: 'Create account' })
      .click()
    await page.getByTestId('emailInput').fill(c.email)
    await page.getByTestId('passwordInput').fill(c.pw)
    await page.getByTestId('date').fill(c.dob)
    await page.getByTestId('nextBtn').click()
    await page.getByTestId('handleInput').waitFor({ timeout: 8000 })
    await page.getByTestId('handleInput').fill(c.handle)
    // Bluesky's resolveHandle availability check is async — clicking
    // Next too early gets swallowed. Retry until the captcha shows up.
    let advanced = false
    for (let attempt = 0; attempt < 6 && !advanced; attempt++) {
      await page.waitForTimeout(700)
      await page.getByTestId('nextBtn').click().catch(() => {})
      for (let w = 0; w < 10; w++) {
        await page.waitForTimeout(200)
        if ((await page.locator('#captcha-iframe').count()) > 0) {
          advanced = true
          break
        }
      }
    }
    console.log('  ▶ captcha shown — solve it, click Submit, then close this window.')
  } catch (err) {
    console.error('  ✕ pre-captcha error: ' + (err instanceof Error ? err.message : err))
  }

  await browser.waitForEvent('disconnected')
  // We can't reliably tell if the captcha succeeded from outside the
  // closed browser, so we count it as "attempted". The operator can
  // verify by trying to sign in below.
  console.log('  ✓ window closed — moving on.')
  succeeded += 1
}

console.log('')
console.log('════════════════════════════════════════════')
console.log('  batch done — ' + succeeded + ' / ' + CANDIDATES.length + ' attempted')
console.log('  back to bskyhq.pages.dev/login to add each')
console.log('  account via app password (Settings → App ')
console.log('  Passwords on bsky.app once signed in).')
console.log('════════════════════════════════════════════')
process.exit(0)
`
}

/**
 * Build the bsky.app URL with a `#bskyhq=<base64-json>` hash payload.
 * The browser extension reads it on page load and drives the form.
 *
 *   https://bsky.app/#bskyhq=eyJlbWFpbCI6Li4ufQ==
 *
 * No-op if the user doesn't have the helper extension installed —
 * Bluesky just shows its normal home, hash is harmless.
 */
export function buildExtensionLaunchUrl(opts: {
  email?: string
  password?: string
  handle?: string
  birthDate?: string
}): string {
  const handleLocal = (opts.handle ?? '').replace(/\.bsky\.social$/i, '')
  const payload = {
    email: opts.email ?? '',
    pw: opts.password ?? '',
    handle: handleLocal,
    dob: opts.birthDate ?? '1990-05-09',
  }
  // btoa needs ASCII; encode UTF-8 first.
  const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
  return `https://bsky.app/#bskyhq=${b64}`
}

/**
 * Build a single-line bash command the operator can paste into
 * Terminal. Sidesteps macOS Gatekeeper + the missing-exec-bit
 * problem entirely — no file is downloaded, so there's no
 * quarantine xattr and no chmod required.
 *
 * The body is base64-encoded so quoting/escaping survives the
 * round trip through both the JS string and the user's clipboard.
 *
 *   bash -c "$(printf %s 'BASE64' | base64 --decode)"
 *
 * On macOS, `base64` is BSD-flavour and uses `--decode` (or `-D`).
 * On Linux it's GNU and uses `-d` (or `--decode`). Both accept
 * `--decode` as long-form, so we use that for portability.
 */
export function buildBashOneLiner(scriptBody: string): string {
  // Wrap the script body in the same setup-Playwright-and-run
  // shell logic as the .command wrapper, then base64-encode the
  // whole thing.
  const wrapped = wrapAsBashCommand(scriptBody)
  // btoa needs ASCII; encode UTF-8 first.
  const b64 = btoa(unescape(encodeURIComponent(wrapped)))
  return `bash -c "$(printf '%s' '${b64}' | base64 --decode)"`
}

/**
 * Wrap a Node ESM signup script in a macOS `.command` file (or
 * Linux `.sh`). Bash heredoc embeds the script. First run npm-
 * installs Playwright into `~/.bskyhq-signup`; subsequent runs
 * hit the cache. macOS double-clicks `.command` files into
 * Terminal.app automatically.
 *
 * Note on Gatekeeper: macOS blocks unsigned downloaded executables
 * unless the user right-clicks → Open the first time. Documented in
 * the Login UI.
 */
export function wrapAsBashCommand(scriptBody: string): string {
  // Heredoc terminator chosen so the body can contain anything.
  const HEREDOC = 'BSKYHQ_SIGNUP_END_OF_SCRIPT_8a3e1f'
  return `#!/bin/bash
# bskyhq signup launcher — generated ${new Date().toISOString()}
DIR="$HOME/.bskyhq-signup"
mkdir -p "$DIR"
cd "$DIR"

trap 'echo ""; echo "✕ FAILED at line $LINENO. Press any key to close…"; read -n 1 -s -r' ERR

if ! command -v node >/dev/null 2>&1; then
  echo "✕ Node.js is required. Install from https://nodejs.org/ and re-run."
  read -n 1 -s -r -p "Press any key to close…"
  exit 1
fi
echo "▶ node: $(node --version)  ($(which node))"
echo "▶ npm:  $(npm --version)"
echo ""

if [ ! -d node_modules/playwright ]; then
  echo "▶ first run — running 'npm install playwright' in $DIR"
  echo "  (this is ~50MB + a few minutes; full output below — leave the window alone)"
  echo "  ─────────────────────────────────────────────"
  npm init -y || { echo "✕ npm init failed"; read -n 1 -s -r -p "Press any key to close…"; exit 1; }
  npm install playwright || { echo "✕ npm install playwright failed"; read -n 1 -s -r -p "Press any key to close…"; exit 1; }
  echo "  ─────────────────────────────────────────────"
  echo "▶ now downloading the chromium browser binary (~140MB)…"
  echo "  ─────────────────────────────────────────────"
  npx --yes playwright install chromium || { echo "✕ chromium download failed"; read -n 1 -s -r -p "Press any key to close…"; exit 1; }
  echo "  ─────────────────────────────────────────────"
  echo "✓ Playwright + chromium installed."
  echo ""
fi

echo "▶ writing signup.mjs"
cat > signup.mjs <<'${HEREDOC}'
${scriptBody}
${HEREDOC}

echo "▶ launching the signup automation — a Chromium window should appear in a few seconds."
echo "▶ if you don't see it, check the Dock and behind other windows."
echo ""
node signup.mjs || { echo "✕ signup.mjs threw — see error above."; }
echo ""
read -n 1 -s -r -p "Press any key to close…"
`
}

/**
 * Wrap a Node ESM signup script in a Windows `.bat` file. Same idea
 * as the macOS `.command`: install Playwright into a user-stable
 * directory once, then run the embedded script. Windows
 * Explorer double-clicks `.bat` files into cmd.exe.
 */
export function wrapAsBat(scriptBody: string): string {
  // .bat files don't have heredocs — write the script via a Node
  // one-liner that decodes a base64 payload to disk.
  const b64 = btoa(unescape(encodeURIComponent(scriptBody)))
  return `@echo off
REM bskyhq signup launcher — generated ${new Date().toISOString()}
setlocal
set "DIR=%USERPROFILE%\\.bskyhq-signup"
if not exist "%DIR%" mkdir "%DIR%"
cd /d "%DIR%"
where node >nul 2>nul
if errorlevel 1 (
  echo Node.js is required. Install from https://nodejs.org/ and re-run.
  pause
  exit /b 1
)
if not exist node_modules\\playwright (
  echo Installing Playwright + chromium (one-time, ~120MB) ...
  call npm init -y >nul 2>nul
  call npm install --silent playwright
  call npx --yes playwright install chromium
)
node -e "require('fs').writeFileSync('signup.mjs', Buffer.from('${b64}', 'base64').toString('utf8'))"
node signup.mjs
echo.
pause
`
}
export async function checkHandleAvailable(handle: string): Promise<{
  handle: string
  available: boolean
  err?: string
}> {
  try {
    const r = await fetch(
      `https://public.api.bsky.app/xrpc/com.atproto.identity.resolveHandle?handle=${encodeURIComponent(handle)}`,
    )
    if (r.status === 200) return { handle, available: false }
    if (r.status >= 400 && r.status < 500) return { handle, available: true }
    return { handle, available: false, err: `unexpected status ${r.status}` }
  } catch (e) {
    return {
      handle,
      available: false,
      err: e instanceof Error ? e.message : String(e),
    }
  }
}
